import { z } from 'zod';

import { isoDatetimeToDate } from '../codecs';

export type SessionResource = z.output<typeof sessionResourceSchema>;
export type SessionResourceDTO = z.input<typeof sessionResourceSchema>;

export type SessionResourceType = SessionResource['type'];

export enum SessionResourceError {
  CAMERA_TOO_LOW = 'CAMERA_TOO_LOW',
  CAMERA_TOO_HIGH = 'CAMERA_TOO_HIGH',
  CAMERA_TOO_CLOSE = 'CAMERA_TOO_CLOSE',
  CAMERA_TOO_FAR = 'CAMERA_TOO_FAR',
  CAMERA_NOT_CENTERED = 'CAMERA_NOT_CENTERED',
  CAMERA_ROTATED = 'CAMERA_ROTATED',
  CAMERA_SKEWED = 'CAMERA_SKEWED',
  CAMERA_UNSTABLE = 'CAMERA_UNSTABLE',
  CAMERA_VIEW_OBSTRUCTED = 'CAMERA_VIEW_OBSTRUCTED',
  CAMERA_LENS_DIRTY = 'CAMERA_LENS_DIRTY',
  CAMERA_LENS_BROKEN = 'CAMERA_LENS_BROKEN',
  CAMERA_VIDEO_CORRUPT = 'CAMERA_VIDEO_CORRUPT',
  UNKNOWN = 'UNKNOWN',
}

export function isSessionResourceError(value: unknown): value is SessionResourceError {
  return Object.values(SessionResourceError).includes(value as SessionResourceError);
}

export enum SessionResourceWarning {
  CAMERA_TOO_LOW = 'CAMERA_TOO_LOW',
  CAMERA_TOO_HIGH = 'CAMERA_TOO_HIGH',
  CAMERA_TOO_CLOSE = 'CAMERA_TOO_CLOSE',
  CAMERA_TOO_FAR = 'CAMERA_TOO_FAR',
  CAMERA_NOT_CENTERED = 'CAMERA_NOT_CENTERED',
  CAMERA_ROTATED = 'CAMERA_ROTATED',
  CAMERA_SKEWED = 'CAMERA_SKEWED',
  CAMERA_UNSTABLE = 'CAMERA_UNSTABLE',
  UNKNOWN = 'UNKNOWN',
}

export function isSessionResourceWarning(value: unknown): value is SessionResourceWarning {
  return Object.values(SessionResourceWarning).includes(value as SessionResourceWarning);
}

export type SessionAccelerationResource = z.output<typeof sessionAccelerationResourceSchema>;
export type SessionAccelerationResourceDTO = z.input<typeof sessionAccelerationResourceSchema>;

export const sessionAccelerationResourceSchema = z.object({
  type: z.literal('acceleration'),
  id: z.uuid(),
  url: z.url(),
  errors: z.array(z.enum(SessionResourceError)),
  warnings: z.array(z.enum(SessionResourceWarning)),
});

export type SessionGyroResource = z.output<typeof sessionGyroResourceSchema>;
export type SessionGyroResourceDTO = z.input<typeof sessionGyroResourceSchema>;

export const sessionGyroResourceSchema = z.object({
  type: z.literal('gyro'),
  id: z.uuid(),
  url: z.url(),
  errors: z.array(z.enum(SessionResourceError)),
  warnings: z.array(z.enum(SessionResourceWarning)),
});

export type SessionVideoResource = z.output<typeof sessionVideoResourceSchema>;
export type SessionVideoResourceDTO = z.input<typeof sessionVideoResourceSchema>;

export const sessionVideoResourceSchema = z.object({
  type: z.literal('video'),
  id: z.uuid(),
  url: z.url(),
  errors: z.array(z.enum(SessionResourceError).catch(SessionResourceError.UNKNOWN)).catch([]),
  warnings: z.array(z.enum(SessionResourceWarning).catch(SessionResourceWarning.UNKNOWN)).catch([]),
  cameraId: z.uuid(),
  end: isoDatetimeToDate.optional(),
  start: isoDatetimeToDate,
});

export const sessionResourceSchema = z.discriminatedUnion('type', [
  sessionAccelerationResourceSchema,
  sessionGyroResourceSchema,
  sessionVideoResourceSchema,
]);

export const SessionResourceUtils = {
  isAccelerationResource(resource: SessionResource): resource is SessionAccelerationResource {
    return resource.type === 'acceleration';
  },
  isGyroResource(resource: SessionResource): resource is SessionGyroResource {
    return resource.type === 'gyro';
  },
  isVideoResource(resource: SessionResource): resource is SessionVideoResource {
    return resource.type === 'video';
  },
};
